import { ApolloCache } from '@apollo/client';
import { styled } from '@mui/material';
import { EmptyStateCard } from 'DesignSystem';
import { EBackgroundTypes, EItemTypes } from 'DesignSystem/types/background.types';
import { EAvailableSizes } from 'DesignSystem/types/size.types';
import PartnerRequestDialog from 'FeatureModules/Partner/Components/PartnerRequests/PartnerRequestDialog';
import PartnerRequestList from 'FeatureModules/Partner/Components/PartnerRequests/PartnerRequestList';
import usePartnerRequestActions from 'FeatureModules/Partner/Components/PartnerRequests/usePartnerRequestActions';
import { IBasePartnerCompany, IPartnerRequest } from 'FeatureModules/Partner/types/partner.types';
import { addRequestToCachePartnerOverview } from 'FeatureModules/Partner/utils/partner.utils';
import useConfig from 'hooks/useConfig';
import React, { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BooleanParam, useQueryParam } from 'use-query-params';
import Icon from 'DesignSystem/Primitives/Icon/Icon';

export const RequestIcon = styled('img')(() => ({
  width: 18,
}));

interface IProps {
  totalRequests?: number;
  requests: IPartnerRequest[];
  partner: IBasePartnerCompany;
}

const PartnerRequestOverview: FC<IProps> = ({ requests, partner, totalRequests }) => {
  const navigate = useNavigate();
  const hasRequests = requests.length > 0;
  const [editRequest, setEditRequest] = useState<IPartnerRequest>();

  const { deletePartnerRequest } = usePartnerRequestActions();
  const { appQueryParams } = useConfig();
  const [createNewRequest, setCreateNewRequest] = useQueryParam<boolean | null | undefined>(
    appQueryParams.createNewRequest,
    BooleanParam
  );

  const handleAllRequestsClick = () => {
    navigate(`/partners/${partner.id}/requests`);
  };

  return (
    <>
      {/* TODO: Use the dialogContext instead of using the dialog directly */}
      {(createNewRequest || editRequest) && (
        <PartnerRequestDialog
          open
          onClose={() => {
            setCreateNewRequest(undefined);
            setEditRequest(undefined);
          }}
          requestedToCompany={partner}
          // Add here the request when it's in edit mode
          initialRequest={editRequest}
          onUpdateCache={(cache: ApolloCache<unknown>, request: IPartnerRequest) =>
            addRequestToCachePartnerOverview(cache, request, partner.id)
          }
        />
      )}
      {!hasRequests ? (
        <EmptyStateCard
          variant={EItemTypes.DOCUMENT}
          onCreateItemClick={() => setCreateNewRequest(true)}
          showSpark={false}
          buttonText="New request"
          data-testid-button="new-request-button"
          title="Request partner documents to manage and prove your impact"
          size={EAvailableSizes.LARGE}
          background={EBackgroundTypes.WORLD}
          buttonIcon={<Icon name="document-request-add" color="white" />}
        />
      ) : (
        <PartnerRequestList
          requests={requests}
          totalItems={totalRequests}
          onClickCreate={() => setCreateNewRequest(true)}
          onClickDelete={deletePartnerRequest}
          onAllRequestsClick={handleAllRequestsClick}
          onClick={request => navigate(`/partners/${partner.id}/requests/${request.id}`)}
        />
      )}
    </>
  );
};

export default PartnerRequestOverview;
